import { useState } from 'react';
import { useLeaseCalculator } from '../../hooks/useLeaseCalculator';
import { useFollowupTracking } from '../../hooks/useFollowupTracking';
import { useLanguage } from '../../i18n/LanguageContext';
import { CalculatorSlider } from './CalculatorSlider';
import { ResultsPanel } from './ResultsPanel';
import { LeadForm } from './LeadForm';
import { FaqSection } from './FaqSection';
import { SeoContent, ContactCta } from './SeoContent';
import { ProductsSection } from './ProductsSection';
import { LanguageSelector } from './LanguageSelector';

export function LeaseCalculator() {
    const { inputs, updateInput, toggleDouchebeurtenMode, results } = useLeaseCalculator();
    const { markSubmitted } = useFollowupTracking(inputs, results);
    const [selectedChoice] = useState(null);
    const { t, formatCurrency, currencySymbol, currencyConfig } = useLanguage();

    const convertPrice = (eurAmount) => (eurAmount * currencyConfig.rate).toFixed(2);

    return (
        <div className="min-h-screen bg-[#FBF7EE]" data-testid="lease-calculator">
            {/* Header */}
            <header className="py-6 px-4 bg-[#3D3B00] sticky top-0 z-50 shadow-md">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <img
                        src="/images/logo.svg"
                        alt="Recra Solutions"
                        className="h-10 sm:h-12 object-contain"
                        style={{ filter: 'brightness(0) invert(1)' }}
                    />
                    <nav className="flex items-center gap-4 md:gap-6">
                        <button
                            onClick={() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' })}
                            className="hidden md:block px-4 py-2 bg-[#4CAF50] text-white font-semibold rounded-lg hover:bg-[#43A047] transition-colors"
                            data-testid="header-contact-btn"
                        >
                            {t('nav_contact')}
                        </button>
                        <LanguageSelector />
                    </nav>
                </div>
            </header>

            {/* Hero: H1 + Intro */}
            <section className="pt-12 lg:pt-20 pb-8 px-4 bg-gradient-to-b from-[#F5ECD7] to-[#FBF7EE]" data-testid="section-hero">
                <div className="max-w-4xl mx-auto text-center">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#3D3B00] font-['Outfit'] mb-6 tracking-tight leading-tight">
                        Wat levert een contactloze douchelezer uw park op? Bereken het direct.
                    </h1>
                    <p className="text-base lg:text-lg text-[#3D3B00]/75 leading-relaxed max-w-3xl mx-auto">
                        Muntjes tellen, sleutels uitgeven, kassa&#39;s legen. Het kost tijd en u loopt omzet mis. Met een contactloze douchelezer betalen gasten per douchebeurt met hun pinpas, creditcard of telefoon. U stelt zelf het tarief in en ziet elke transactie live in uw dashboard. Vul hieronder uw aantallen in en zie binnen een minuut wat kopen of leasen u per jaar oplevert.
                    </p>
                </div>
            </section>

            {/* Lease Calculator Section */}
            <section id="lease-section" className="py-12 lg:py-16 px-4 bg-[#FBF7EE]">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
                        {/* Input Panel */}
                        <div className="lg:col-span-7 bg-white rounded-2xl shadow-lg border border-gray-100 p-6 lg:p-8">
                            <div className="flex items-center gap-3 mb-8">
                                <div className="w-10 h-10 bg-[#4CAF50]/10 rounded-lg flex items-center justify-center">
                                    <svg className="w-5 h-5 text-[#4CAF50]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                    </svg>
                                </div>
                                <div>
                                    <h2 className="text-xl font-bold text-[#3D3B00] font-['Outfit']">
                                        {t('calc_your_situation')}
                                    </h2>
                                    <p className="text-sm text-[#3D3B00]/50">
                                        {t('calc_adjust_sliders')}
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                <CalculatorSlider
                                    testId="slider-douches"
                                    label={t('slider_readers')}
                                    value={inputs.aantalDouches}
                                    onChange={(val) => updateInput('aantalDouches', val)}
                                    min={1}
                                    max={40}
                                    step={1}
                                />

                                <CalculatorSlider
                                    testId="slider-douchebeurten"
                                    label={inputs.douchebeurtenPerDag ? t('slider_showers_day') : t('slider_showers_month')}
                                    value={inputs.douchebeurtenWaarde}
                                    onChange={(val) => updateInput('douchebeurtenWaarde', val)}
                                    min={inputs.douchebeurtenPerDag ? 5 : 100}
                                    max={inputs.douchebeurtenPerDag ? 100 : 3000}
                                    step={inputs.douchebeurtenPerDag ? 5 : 50}
                                    toggleLabel={inputs.douchebeurtenPerDag ? t('slider_per_month') : t('slider_per_day')}
                                    onToggle={toggleDouchebeurtenMode}
                                    showToggle={true}
                                    infoText={`= ${Math.round(inputs.douchebeurtenWaarde / inputs.aantalDouches)} ${t('slider_per_reader_per')} ${inputs.douchebeurtenPerDag ? t('slider_per_day') : t('slider_per_month')}`}
                                />

                                <CalculatorSlider
                                    testId="slider-prijs"
                                    label={t('slider_price')}
                                    value={inputs.prijsPerMinuut}
                                    onChange={(val) => updateInput('prijsPerMinuut', val)}
                                    min={0.20}
                                    max={1.50}
                                    step={0.05}
                                    prefix={currencySymbol}
                                    displayValue={convertPrice(inputs.prijsPerMinuut)}
                                    displayMin={convertPrice(0.20)}
                                    displayMax={convertPrice(1.50)}
                                    infoText={`= ${currencySymbol}${convertPrice(inputs.prijsPerMinuut * inputs.gemiddeldeDouchetijd)} ${t('slider_per_shower')}`}
                                />

                                <CalculatorSlider
                                    testId="slider-douchetijd"
                                    label={t('slider_shower_time')}
                                    value={inputs.gemiddeldeDouchetijd}
                                    onChange={(val) => updateInput('gemiddeldeDouchetijd', val)}
                                    min={5}
                                    max={10}
                                    step={1}
                                    suffix={` ${t('slider_min')}`}
                                />

                                <CalculatorSlider
                                    testId="slider-maanden"
                                    label={t('slider_months_open')}
                                    value={inputs.maandenOpen}
                                    onChange={(val) => updateInput('maandenOpen', val)}
                                    min={1}
                                    max={12}
                                    step={1}
                                />

                                <CalculatorSlider
                                    testId="slider-bezettingsgraad"
                                    label={t('slider_occupancy')}
                                    value={inputs.bezettingsgraad}
                                    onChange={(val) => updateInput('bezettingsgraad', val)}
                                    min={40}
                                    max={100}
                                    step={5}
                                    suffix="%"
                                    contractLabels={{
                                        55: t('slider_conservative'),
                                        65: t('slider_realistic'),
                                        100: t('slider_optimistic')
                                    }}
                                />
                            </div>

                            {/* Contract info + Garantie */}
                            <div className="mt-8 space-y-3">
                                <div className="p-4 bg-white/60 rounded-xl border border-[#3D3B00]/10">
                                    <div className="flex flex-wrap items-center gap-2 text-sm text-[#3D3B00]/60">
                                        <span className="font-semibold text-[#3D3B00]">{t('lease_fixed_price').replace('{0}', formatCurrency(39))}</span>
                                        <span>&bull;</span>
                                        <span>{t('lease_contract_info')}</span>
                                        <span>&bull;</span>
                                        <span>{t('lease_early_exit')}</span>
                                    </div>
                                </div>

                                {/* No Nonsense Garantie Toggle */}
                                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                                    inputs.garantie
                                        ? 'bg-[#4CAF50]/5 border-[#4CAF50]/30'
                                        : 'bg-white/60 border-[#3D3B00]/10 hover:border-[#3D3B00]/20'
                                }`} data-testid="garantie-toggle">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={inputs.garantie}
                                            onChange={(e) => updateInput('garantie', e.target.checked)}
                                            className="w-4 h-4 accent-[#4CAF50] rounded"
                                        />
                                        <div>
                                            <div className="font-semibold text-[#3D3B00] text-sm">{t('garantie_label')}</div>
                                            <div className="text-xs text-[#3D3B00]/50">{t('garantie_desc')}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-[#4CAF50]">+{formatCurrency(3)}</div>
                                        <div className="text-xs text-[#3D3B00]/40">{t('garantie_per_terminal')}</div>
                                    </div>
                                </label>

                                {/* Muurbevestiging Toggle (alleen voor koop-scenario) */}
                                <label className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer transition-all ${
                                    inputs.muurbevestiging
                                        ? 'bg-[#3D3B00]/5 border-[#3D3B00]/30'
                                        : 'bg-white/60 border-[#3D3B00]/10 hover:border-[#3D3B00]/20'
                                }`} data-testid="muurbevestiging-toggle">
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={inputs.muurbevestiging}
                                            onChange={(e) => updateInput('muurbevestiging', e.target.checked)}
                                            className="w-4 h-4 accent-[#3D3B00] rounded"
                                            data-testid="muurbevestiging-checkbox"
                                        />
                                        <div>
                                            <div className="font-semibold text-[#3D3B00] text-sm">Muurbevestiging (kunststof)</div>
                                            <div className="text-xs text-[#3D3B00]/50">Perfect passend rond de S1U2, eenmalig per terminal</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-[#3D3B00]">+{formatCurrency(59)}</div>
                                        <div className="text-xs text-[#3D3B00]/40">per terminal</div>
                                    </div>
                                </label>
                            </div>
                        </div>

                        {/* Results Panel */}
                        <div className="lg:col-span-5 lg:sticky lg:top-24 space-y-6">
                            <ResultsPanel results={results} />
                        </div>
                    </div>
                </div>
            </section>

            {/* SEO content sections (4 H2s) */}
            <SeoContent />

            {/* Producten (Pinterminal + Muurbevestiging) */}
            <ProductsSection />

            {/* FAQ */}
            <FaqSection />

            {/* CTA block */}
            <ContactCta />

            {/* Lead Form Section */}
            <section id="lead-form" className="py-16 lg:py-24 px-4 bg-[#FBF7EE]">
                <div className="max-w-3xl mx-auto">
                    <LeadForm
                        calculatorInputs={inputs}
                        calculatorResults={results}
                        selectedChoice={selectedChoice}
                        onSubmitted={markSubmitted}
                    />
                </div>
            </section>

            {/* Footer */}
            <footer className="py-10 px-4 bg-[#3D3B00]">
                <div className="max-w-7xl mx-auto text-center space-y-4">
                    <p className="text-sm">
                        <a
                            href="https://recrasolutions.com/oplossingen/contactloos-betalen-met-douchelezers/"
                            className="text-[#F5ECD7] hover:text-[#4CAF50] underline transition-colors"
                            data-testid="footer-parent-link"
                        >
                            Meer over contactloos betalen met douchelezers
                        </a>
                    </p>
                    <p className="text-sm text-[#F5ECD7]/70">
                        {t('footer')}
                    </p>
                </div>
            </footer>
        </div>
    );
}
